import type { IScopeRelatedItem } from "@/api";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDistanceToNowStrict } from "date-fns";
import { FileSearch2, ScanSearch } from "lucide-react";
import { EmptySectionState } from "./related/EmptySectionState";
import { RelatedItemCard, StatusDot } from "./related/RelatedItemCard";
import { RelatedSectionHeader } from "./related/RelatedSectionHeader";

interface RelatedScopesSectionProps {
    isLoading: boolean;
    scopes: IScopeRelatedItem[];
    onScopeClick?: (scope: IScopeRelatedItem) => void;
}

const defaultOnScopeClick = (scope: IScopeRelatedItem) => {
    window.open(`/organizations/scopes/${scope.id}`, "_blank");
};

const formatScopeProgress = (scope: IScopeRelatedItem): string => {
    if (scope.status === "processing") return "Running...";
    if (scope.status === "pending") return "Waiting...";
    if (scope.status === "failed") return "Failed";
    const noun = scope.clause_count === 1 ? "clause found" : "clauses found";
    return `${scope.clause_count} ${noun}`;
};

export const RelatedScopesSection = ({
    isLoading,
    scopes,
    onScopeClick = defaultOnScopeClick,
}: RelatedScopesSectionProps) => {
    if (isLoading) {
        return (
            <>
                <RelatedSectionHeader title="Related Scopes" count={0} />
                <div className="mb-4 flex flex-col gap-1.5">
                    {Array.from({ length: 3 }, (_, i) => (
                        <Skeleton
                            key={i}
                            data-testid="related-scopes-skeleton-row"
                            className="h-13 w-full rounded-[10px]"
                        />
                    ))}
                </div>
            </>
        );
    }

    if (scopes.length === 0) {
        return (
            <>
                <RelatedSectionHeader title="Related Scopes" count={0} />
                <EmptySectionState
                    icon={FileSearch2}
                    title="No related scopes yet"
                    description="Create a scope from this document to analyze its clauses."
                />
            </>
        );
    }

    return (
        <>
            <RelatedSectionHeader
                title="Related Scopes"
                count={scopes.length}
            />
            <div className="mb-4 flex flex-col gap-1.5">
                {scopes.map((scope) => (
                    <RelatedItemCard
                        key={scope.id}
                        icon={ScanSearch}
                        iconColorClass="bg-primary/10 text-primary"
                        title={scope.document_title}
                        metadata={[
                            scope.clause_checklist.name,
                            formatScopeProgress(scope),
                            formatDistanceToNowStrict(
                                new Date(scope.created_at),
                                { addSuffix: true }
                            ),
                        ]}
                        statusIndicator={<StatusDot status={scope.status} />}
                        onClick={() => onScopeClick(scope)}
                    />
                ))}
            </div>
        </>
    );
};
